import { useJobStore } from "../store/Jobs";
import { Tooltip } from "@mui/material";
import { JobDocumentTypeEnum } from "../pages/job-page/job-document/job-document.types";
import { jobDocumentTypeNamePresets } from "../store/Jobs/utils";
import { useCentralStore } from "../store/Central";
import { supabase } from "@/lib/supabase/supabaseClient";
import { usePatientStore } from "../store/Patient";
import { isDeveloper } from "@/lib/utils/is-app-user";
import { cn } from "../lib/utils/cn";
import { useAttributesStore } from "../store/Attributes/attributes.store";
import {
	ONBOARDING_STEPS,
	ONBOARDING_STATUS,
	MIGRATION_DECISION,
} from "../constants/onboarding";
import { SupportPrintGrantButton } from "./layout-components/support-print-grant-button.component";

const ONBOARDING_STEP_LABELS: Record<string, string> = {
	[ONBOARDING_STEPS.PERSONAL_INFO]: "Persönliche Daten",
	[ONBOARDING_STEPS.ORGANIZATION_INFO]: "Labor-Informationen",
	[ONBOARDING_STEPS.REGION]: "Region",
	[ONBOARDING_STEPS.BANK_ACCOUNT]: "Bankverbindung",
	[ONBOARDING_STEPS.PRICE_LIST]: "Preisliste",
	[ONBOARDING_STEPS.MIGRATION_DECISION]: "Migration",
	[ONBOARDING_STEPS.TEAM_INVITE]: "Team einladen",
};

const ONBOARDING_STATUS_LABELS: Record<string, string> = {
	[ONBOARDING_STATUS.COMPLETED]: "✅ Abgeschlossen",
	[ONBOARDING_STATUS.SKIPPED]: "⏭️ Übersprungen",
	[ONBOARDING_STATUS.OPEN]: "⏳ Offen",
};

const MIGRATION_DECISION_LABELS: Record<string, string> = {
	[MIGRATION_DECISION.MIGRATE_FROM_DLXP]: "Migration von DLXP",
	[MIGRATION_DECISION.NO_MIGRATION]: "Keine Migration",
	[MIGRATION_DECISION.MIGRATION_DONE]: "Migration abgeschlossen",
};

const getAttributeLabel = (key: string): string => {
	return ONBOARDING_STEP_LABELS[key] ?? key;
};

const getValueLabel = (key: string, value: string | number): string => {
	const stringValue = String(value);
	if (Object.values(ONBOARDING_STEPS).includes(key as any)) {
		return ONBOARDING_STATUS_LABELS[stringValue] ?? stringValue;
	}
	if (key === ONBOARDING_STEPS.MIGRATION_DECISION) {
		return (
			MIGRATION_DECISION_LABELS[stringValue] ??
			ONBOARDING_STATUS_LABELS[stringValue] ??
			stringValue
		);
	}
	return stringValue;
};

export const IdInfo: React.FC = () => {
	const {
		userId,
		organizationId,
		client,
		connectedOrganizationEmail,
		subscription,
		subscriptionTier,
		needsPasswordSetup,
	} = useCentralStore((state) => ({
		userId: state.userId,
		organizationId: state.organization?.id,
		client: state.client,
		clientId: state.clientId,
		connectedOrganizationEmail: state.connectedOrganizationEmail,
		subscription: state.subscription,
		subscriptionTier: state.subscriptionTier,
		needsPasswordSetup: state.needsPasswordSetup,
	}));
	const { jobId, jobPatientId, jobDocuments } = useJobStore((state) => ({
		jobId: state.job?.id,
		jobPatientId: state.job?.patient_id,
		jobDocuments: Object.values(state.jobDocuments)
			.map((jobDocument) => ({
				id: jobDocument.id,
				type: jobDocument.type,
			}))
			.sort((a, b) => a.id - b.id),
	}));

	const { patientsLookup, patientName } = usePatientStore((state) => ({
		patientsLookup: state.patientsLookup,
		patientName: state.patientName,
	}));

	const { attributes } = useAttributesStore((state) => ({
		attributes: state.attributes,
	}));

	const isServiceRoleKeyDefined = import.meta.env
		.VITE_PROD_SUPABASE_SERVICE_ROLE_KEY;

	return (
		<div
			className={cn(
				"bg-[#F5F5F4] text-xs flex flex-row gap-2 px-2",
				isServiceRoleKeyDefined && "bg-red-500"
			)}
		>
			{isDeveloper(import.meta.env, "") && ( //no mail because we don't want that in production
				<>
					<button
						onClick={async () => {
							if (!userId || !organizationId) {
								alert("User ID or organization ID is not set");
								return;
							}
							//@ts-ignore not needed in production
							const { error } = await supabase.rpc(
								"create_demo_data",
								{
									p_user_id: userId,
									p_organization_id: organizationId,
								}
							);
							if (error) {
								alert(
									`Failed to create demo data: ${error.message}`
								);
								return;
							}
							window.location.reload();
						}}
					>
						Create Demo Data
					</button>
					<button
						onClick={async () => {
							if (!userId || !organizationId) {
								alert("User ID or organization ID is not set");
								return;
							}
							//@ts-ignore not needed in production
							const { error } = await supabase.rpc(
								"delete_demo_data",
								{
									p_user_id: userId,
									p_organization_id: organizationId,
								}
							);
							if (error) {
								alert(
									`Failed to delete demo data: ${error.message}`
								);
								return;
							}
							window.location.reload();
						}}
					>
						Delete Demo Data
					</button>
				</>
			)}
			<SupportPrintGrantButton />
			<b>Tier:</b>
			{subscriptionTier ?? "null"}
			<b>NeedsPW:</b>
			{needsPasswordSetup === null
				? "null"
				: needsPasswordSetup
					? "true"
					: "false"}
			<b>User:</b>
			{userId}
			<b>Org:</b>
			{organizationId}
			<b>Client:</b> {client?.id}
			<b>Connected Organization ID:</b>{" "}
			{client?.connected_organization_id}
			<b>Connected Organization Email:</b> {connectedOrganizationEmail}
			<b>Job:</b> {jobId}
			<Tooltip
				title={
					jobPatientId && patientsLookup[jobPatientId] ? (
						<div className="flex flex-col gap-2 text-lg">
							{Object.entries(patientsLookup[jobPatientId])
								.filter(([key]) => key !== "search_vector")
								.sort(([a], [b]) => a.localeCompare(b))
								.map(([key, value]) => (
									<span key={key}>
										<b>{key}:</b> {value ?? "null"}
									</span>
								))}
						</div>
					) : (
						<span>No patient assigned</span>
					)
				}
			>
				<b>Patient:</b>
			</Tooltip>
			<Tooltip
				title={
					<div>
						{jobDocuments.map((jobDocument) => (
							<div
								key={jobDocument.id}
								className="flex flex-row gap-2 text-lg"
							>
								{jobDocumentTypeNamePresets(
									jobDocument.type as JobDocumentTypeEnum,
									null
								)}
								: {jobDocument.id}
							</div>
						))}
					</div>
				}
			>
				<b>JobDocuments</b>
			</Tooltip>
			<Tooltip
				title={
					<div className="flex flex-col gap-2 text-lg">
						<span>
							<b>Status:</b> {subscription?.status}
						</span>
						<span>
							<b>Quantity:</b> {subscription?.quantity}
						</span>
						<span>
							<b>Exisiting invoices:</b>{" "}
							{subscription?.existing_invoices ? "true" : "false"}
						</span>
						<span>
							<b>Subscription ID:</b>{" "}
							{subscription?.subscription_id}
						</span>
						<span>
							<b>Customer ID:</b> {subscription?.customer_id}
						</span>
						<span>
							<b>Currency:</b> {subscription?.currency}
						</span>
						<span>
							<b>Current period start:</b>{" "}
							{subscription?.period_start}
						</span>
						<span>
							<b>Current period end:</b>{" "}
							{subscription?.period_end}
						</span>
						<span>
							<b>Trial period end:</b>{" "}
							{subscription?.trial_period_end}
						</span>
						<span>
							<b>Cancel at period end:</b>{" "}
							{subscription?.cancel_at_period_end == true
								? "true"
								: "false"}
						</span>
						<span>
							<b>Canceled at:</b> {subscription?.canceled_at}
						</span>
					</div>
				}
			>
				<b>Subscription</b>
			</Tooltip>
			<Tooltip
				title={
					<div className="flex flex-col gap-2 text-lg overflow-y-auto max-h-[500px]">
						{Object.values(patientsLookup)
							.sort((a, b) =>
								(a.first_name ?? "").localeCompare(
									b.first_name ?? ""
								)
							)
							.map((patient: any) => (
								<div
									key={patient?.code}
									className="flex flex-col gap-2 text-sm border-b border-gray-200 pb-2"
								>
									<div className="font-bold">
										{patientName(patient)}
									</div>
									<div>Code: {patient?.code}</div>
									<div>Title: {patient?.title}</div>
									<div>City: {patient?.city ?? "N/A"}</div>
									<div>
										Postal Code:{" "}
										{patient?.postal_code ?? "N/A"}
									</div>
									<div>
										Street: {patient?.street ?? "N/A"}
									</div>
									<div>
										Birth Date:{" "}
										{patient?.birth_date ?? "N/A"}
									</div>
								</div>
							))}
					</div>
				}
			>
				<b>Patients</b>
			</Tooltip>
			<Tooltip
				title={
					<div className="flex flex-col gap-2 text-lg overflow-y-auto max-h-[500px]">
						{Object.entries(attributes)
							.sort(([a], [b]) => a.localeCompare(b))
							.map(([key, value]) => (
								<div
									key={key}
									className="flex flex-row gap-2 text-sm"
								>
									<span className="font-bold">
										{getAttributeLabel(key)}:
									</span>
									<span>{getValueLabel(key, value)}</span>
								</div>
							))}
						{Object.keys(attributes).length === 0 && (
							<span className="text-gray-400">No attributes</span>
						)}
					</div>
				}
			>
				<b>Attributes</b>
			</Tooltip>
			{isServiceRoleKeyDefined && (
				<div className="text-white font-bold">
					LÖSCHE DEN SERVICE ROLE KEY VOM ENV FILE!
				</div>
			)}
		</div>
	);
};
